import { loadStripe } from '@stripe/stripe-js';
import { Cart, CartItem } from './cart';

// Get Stripe publishable key from environment variables
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Initialize Stripe promise
let stripePromise: ReturnType<typeof loadStripe> | null = null;

// Get Stripe instance
export const getStripe = () => {
  if (!stripePublishableKey) {
    console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not set');
    return null;
  }
  
  if (!stripePromise) {
    stripePromise = loadStripe(stripePublishableKey);
  }
  return stripePromise;
};

// Convert cart items to secure checkout items (only IDs and quantities)
export const cartToStripeItems = (cart: Cart) => {
  // SECURITY: Only send product IDs and quantities to server
  // Server will validate all product data (prices, availability, etc.)
  return cart.items.map((item: CartItem) => ({
    productId: item.id,
    quantity: item.quantity,
    size: item.size, // Include size for server validation
  }));
};

// Get user data if available
const getUserData = () => {
  try {
    // Check for user information in localStorage or context
    const userDataString = localStorage.getItem('user');
    if (userDataString) {
      return JSON.parse(userDataString);
    }
    return null;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

// Get CSRF token or generate a fallback
const getCsrfToken = async (): Promise<string> => {
  try {
    // Try to get token from cookie directly
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrf_token') {
        return value;
      }
    }
    
    // If not found, try to fetch a new one from the server
    const response = await fetch('/api/csrf-token');
    
    if (!response.ok) {
      console.error(`Failed to get CSRF token: ${response.status} ${response.statusText}`);
      return ''; // Return empty string instead of trying to parse invalid JSON
    }
    
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.error('CSRF token endpoint did not return JSON');
      return '';
    }
    
    const data = await response.json();
    return data.token || '';
  } catch (error) {
    console.error('Failed to get CSRF token:', error);
    // Return empty string if failed
    return '';
  }
};

// Create a checkout session
export const createCheckoutSession = async (cart: Cart) => {
  try {
    // Get CSRF token for security
    const csrfToken = await getCsrfToken();
    
    // Get user data if available
    const userData = getUserData();
    
    const response = await fetch('/api/checkout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-csrf-token': csrfToken || '', 
      },
      body: JSON.stringify({
        items: cartToStripeItems(cart),
        // SECURITY: URLs are now server-controlled, not client-controlled
        // This prevents open redirect vulnerabilities and session ID leakage
        userData,
      }),
    });

    if (!response.ok) {
      // Check content type before trying to parse JSON
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status} ${response.statusText}`);
      } else {
        // Handle non-JSON error response
        const errorText = await response.text();
        console.error('Non-JSON error response:', errorText);
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
    }

    // Check content type before parsing JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.error('Checkout endpoint did not return JSON');
      throw new Error('Invalid response format from server');
    }
    
    const session = await response.json();
    
    if (!session || !session.id) {
      throw new Error('Failed to create checkout session');
    }

    return session;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

export default {
  getStripe,
  createCheckoutSession,
  cartToStripeItems,
}; 